import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {Observable, Subscription} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    DocumentTypesEnum,
    IdentityDocumentTypesEnum,
    DocumentStatusTriggersEnum,
    AuthApiService,
    FileStorageService,
    CompanyDocumentsService,
    IndividualDocumentsService,
    IndividualIdentityDetailsService,
    CompanyExtendedModel,
    CompanyDocumentModel,
    IndividualDocumentModel,
    IndividualExtendedModel,
    CompanyDocumentStatusTriggerModel,
    IndividualDocumentStatusTriggerModel,
    CompanyDocumentsAtomicService,
    IndividualDocumentsAtomicService
} from '@spa/api';

import {Helper} from '../../../../shared/classes';
import {FormComponent} from '../../../../shared/widgets';
import {FormInterface} from '../../../../shared/interfaces';
import {DialogActionsEnum, DialogComponent, DialogConfigInterface} from '../../../../shared/components';

import {AddDocumentForm} from '../../forms';
import {AddDocumentFormErrorStatesEnum} from '../../enums';

@Component({
    selector: 'app-add-document',
    templateUrl: './add-document.component.html',
    styleUrls: ['./add-document.component.scss']
})
export class AddDocumentComponent
    extends DialogComponent<AddDocumentComponent, IndividualDocumentModel | CompanyDocumentModel>

    implements FormInterface, OnDestroy {

    private requestSubscription: Subscription;
    private document: IndividualDocumentModel | CompanyDocumentModel;

    public hiddenSubType: boolean;
    public addDocumentForm: AddDocumentForm;
    public dialogActionsEnum: typeof DialogActionsEnum;
    public documentTypesEnum: typeof DocumentTypesEnum;
    public identityDocumentTypesEnum: typeof IdentityDocumentTypesEnum;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            client: IndividualExtendedModel | CompanyExtendedModel,
            config: DialogConfigInterface,
            isCorporateClient: boolean
        },
        public dialogRef: MatDialogRef<AddDocumentComponent>,
        public individualDocumentsService: IndividualDocumentsService,
        public fileStorageService: FileStorageService,
        public authenticationService: AuthApiService,
        public identityDetailsService: IndividualIdentityDetailsService,
        public companyDocumentsService: CompanyDocumentsService,
        public companyDocumentsAtomicService: CompanyDocumentsAtomicService,
        public individualDocumentsAtomicService: IndividualDocumentsAtomicService
    ) {
        super(dialogRef);

        this.config = data.config;

        this.documentTypesEnum = DocumentTypesEnum;
        this.identityDocumentTypesEnum = IdentityDocumentTypesEnum;
        this.dialogActionsEnum = DialogActionsEnum;
        this.document = null;
        this.hiddenSubType = false;

        this.initForm();
    }

    private changeDocumentStatus(
        registrationResponse: ApiResponse<ApiError[] | IndividualDocumentModel | CompanyDocumentModel>
    ): Observable<ApiResponse<ApiError[] | IndividualDocumentModel | CompanyDocumentModel>> {
        if (registrationResponse.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper()
                .createErrorResponseObservable<IndividualDocumentModel | CompanyDocumentModel>(registrationResponse);
        }

        const documentModel = !this.data.isCorporateClient ? (registrationResponse.data as IndividualDocumentModel) :
            (registrationResponse.data as CompanyDocumentModel);

        if (!this.addDocumentForm.isSetAsActiveChecked()) {
            return Helper.createSuccessResponseObservable<IndividualDocumentModel | CompanyDocumentModel>(documentModel);
        }

        let documentStatusTriggerModel = null;
        let request = null;

        if (!this.data.isCorporateClient) {
            documentStatusTriggerModel = new IndividualDocumentStatusTriggerModel(
                documentModel.id,
                DocumentStatusTriggersEnum.Approved
            );

            request = this.individualDocumentsService.changeStatus(documentStatusTriggerModel);
        } else {
            documentStatusTriggerModel = new CompanyDocumentStatusTriggerModel(
                documentModel.id,
                DocumentStatusTriggersEnum.Approved
            );

            request = this.companyDocumentsService.changeStatus(documentStatusTriggerModel);
        }

        return request.pipe(
            map((statusResponse: ApiResponse<ApiError[] | null>) => {
                if (statusResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<null>(
                        statusResponse,
                        AddDocumentFormErrorStatesEnum.UpdateDocumentToApprovedFailed
                    );
                }

                documentModel.status = Helper.enumsHelper().getDocumentStatusByTrigger(
                    DocumentStatusTriggersEnum.Approved
                );

                return Helper.createSuccessResponse<IndividualDocumentModel | CompanyDocumentModel>(documentModel);
            })
        );
    }

    ngOnDestroy(): void {
        if (this.requestSubscription) {
            this.requestSubscription.unsubscribe();
        }
    }

    getFormMessages(controlLabel?: string, controlLength?: number): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel, controlLength);
    }

    resetDocumentsTupleItem(documentSubType: string): void {
        const types = [IdentityDocumentTypesEnum.IdentityCard, IdentityDocumentTypesEnum.DrivingLicense] as string[];

        if (types.indexOf(documentSubType) <= 0) {
            this.addDocumentForm.resetDocumentsTuplesItem(1);
        }
    }

    onDocumentTypeChange(selfInit: boolean, formType: string): void {
        this.hiddenSubType = (
            (formType === DocumentTypesEnum.Selfie) ||
            (formType === DocumentTypesEnum.Other) ||
            (Helper.enumsHelper().getCompanyDocumentTypes().getTypeKeys().indexOf(formType) >= 0)
        );

        this.addDocumentForm.initForm(selfInit, formType);
    }

    initForm(): void {
        this.addDocumentForm = new AddDocumentForm(
            this.data.client,
            this.authenticationService,
            this.identityDetailsService,
            this.data.isCorporateClient,
            this.companyDocumentsAtomicService,
            this.individualDocumentsAtomicService
        );

        this.addDocumentForm.initForm();
    }

    validateForm(): boolean {
        return this.addDocumentForm.validateForm(this.getFormMessages);
    }

    onFormSubmit(action?: number): void {
        if (action !== this.dialogActionsEnum.Confirm) {
            this.dialogRef.close(this.document);
        } else {
            this.addDocumentForm.initForm(false);

            if (this.validateForm()) {
                this.startLoading(() => {
                    this.requestSubscription = this.addDocumentForm.createRequest().pipe(
                        switchMap(this.changeDocumentStatus.bind(this))
                    ).subscribe(
                        this.dialogSubscribeHandler.bind(
                            this,
                            (data: IndividualDocumentModel | CompanyDocumentModel) => {
                                this.document = data;
                            },
                            null
                        )
                    );
                });
            }
        }
    }
}
